import dayjs from "dayjs";
import React, { useContext, useState, useEffect } from "react";
import GlobalContext from "../context/GlobalContext";
import { AuthContext } from "react-oauth2-code-pkce";
import EventsService from "../services/events-service";

const PIXELS_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * 60;

function minutesFromTime(time) {
  if (!time) return 0;
  const [h, m] = String(time).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function eventTimeRange(evt) {
  const start = minutesFromTime(evt.startTime);
  let end = evt.endTime ? minutesFromTime(evt.endTime) : MINUTES_PER_DAY;
  if (evt.endTime === "23:59") end = MINUTES_PER_DAY;
  if (end <= start) end = MINUTES_PER_DAY;
  return { start, end };
}

function eventsOverlap(a, b) {
  return a.start < b.end && b.start < a.end;
}

function layoutTimedEvents(events) {
  const items = events.map((evt, index) => {
    const { start, end } = eventTimeRange(evt);
    return { evt, index, start, end, column: 0, columnCount: 1 };
  });
  const sorted = [...items].sort(
    (a, b) => a.start - b.start || a.end - b.end || a.index - b.index
  );

  const columnEnd = [];
  for (const item of sorted) {
    let col = columnEnd.findIndex((end) => end <= item.start);
    if (col === -1) {
      col = columnEnd.length;
      columnEnd.push(item.end);
    } else {
      columnEnd[col] = item.end;
    }
    item.column = col;
  }

  const parent = items.map((_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const unite = (i, j) => {
    const a = find(i);
    const b = find(j);
    if (a !== b) parent[a] = b;
  };
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      if (eventsOverlap(sorted[i], sorted[j])) {
        unite(sorted[i].index, sorted[j].index);
      }
    }
  }
  const clusterCols = {};
  items.forEach((item) => {
    const root = find(item.index);
    clusterCols[root] = Math.max(clusterCols[root] || 0, item.column + 1);
  });
  items.forEach((item) => {
    item.columnCount = clusterCols[find(item.index)] || 1;
  });
  return items;
}

export default function Day({ day, viewMode = "month" }) {
  const [dayEvents, setDayEvents] = useState([]);
  const [dragDropError, setDragDropError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showUncoupleDialog, setShowUncoupleDialog] = useState(false);
  const [pendingUncoupleData, setPendingUncoupleData] = useState(null);
  const { labels, setLabels, monthIndex, setMonthIndex, isReadonly, activeCalendarId } = useContext(GlobalContext);
  const { token, tokenData } = useContext(AuthContext);
  
  // Check if we're in time-based view (day or week)
  const isTimeBasedView = viewMode === "day" || viewMode === "week";

  // Fade out drag & drop error after 3 seconds
  useEffect(() => {
    if (dragDropError) {
      const timer = setTimeout(() => setDragDropError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [dragDropError]);

  const {
    setDaySelected,
    setShowEventModal,
    filteredEvents,
    setSelectedEvent,
    dispatchCalEvent,
    fetchEvents, // <-- Get fetchEvents function from context
    setContextMenu,
  } = useContext(GlobalContext);

  useEffect(() => {
    // Sort events by start time (if present), otherwise by id
    const sorted = [...filteredEvents]
      .filter(evt => dayjs(evt.day).format("DD-MM-YY") === day.format("DD-MM-YY"))
      .sort((a, b) => {
        if (a.startTime && b.startTime) {
          return a.startTime.localeCompare(b.startTime);
        } else if (a.startTime) {
          return -1;
        } else if (b.startTime) {
          return 1;
        } else {
          return (a.id || 0) - (b.id || 0);
        }
      });
    // Remove duplicate events with the same id for this day (keep first occurrence)
    const uniqueById = [];
    const seenIds = new Set();
    for (const evt of sorted) {
      if (!seenIds.has(evt.id)) {
        uniqueById.push(evt);
        seenIds.add(evt.id);
      }
    }
    setDayEvents(uniqueById);
  }, [filteredEvents, day]);

  function getCurrentDayClass() {
    return day.format("DD-MM-YY") === dayjs().format("DD-MM-YY")
      ? "bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center"
      : "";
  }

  function isOverlap(eventA, eventB) {
    if (!eventA.startTime || !eventA.endTime || !eventB.startTime || !eventB.endTime) return false;
    return eventsOverlap(eventTimeRange(eventA), eventTimeRange(eventB));
  }

  function eventBlockStyle(item, bgColor) {
    if (!isTimeBasedView) {
      return { background: bgColor };
    }
    const height = Math.max(((item.end - item.start) / 60) * PIXELS_PER_HOUR - 1, 24);
    const leftPct = (item.column / item.columnCount) * 100;
    const widthPct = 100 / item.columnCount;
    return {
      position: "absolute",
      top: `${(item.start / 60) * PIXELS_PER_HOUR}px`,
      height: `${height}px`,
      left: `calc(${leftPct}% + 1px)`,
      width: `calc(${widthPct}% - 3px)`,
      background: bgColor,
      boxSizing: "border-box",
    };
  }

  // Handle the confirmation for uncoupling a projected event
  const handleUncoupleConfirm = async () => {
    if (!pendingUncoupleData) return;
    
    try {
      setDragDropError(null);
      const { originalEvent, newDay } = pendingUncoupleData;
      
      // Create new event data without recurring rules
      const newEventData = {
        ...originalEvent,
        day: newDay,
        projection: false,
        recurrenceType: 'NONE',
        recurrenceInterval: null,
        recurrenceEndDate: null
      };
      
      // Check for overlap with other events on the target day before proceeding
      const targetDayEvents = dayEvents.filter(evt => evt.day === newDay);
      const overlaps = targetDayEvents.some(evt => isOverlap(evt, newEventData));
      if (overlaps) {
        setDragDropError("Tijd overlapt met een andere afspraak!");
        setShowUncoupleDialog(false);
        setPendingUncoupleData(null);
        return;
      }
      
      // Save scroll position before refetch
      const calendarEl = document.getElementById('main-calendar-scroll');
      const scrollTop = calendarEl ? calendarEl.scrollTop : null;

      // Call the uncouple API
      await EventsService.uncoupleProjectedEvent(originalEvent, newEventData, token);
      
      // Always refetch events from the API after uncoupling
      if (typeof fetchEvents === 'function') {
        setTimeout(() => fetchEvents(), 0);
      }
      
      // Restore scroll after DOM update
      if (calendarEl && scrollTop !== null) {
        setTimeout(() => { calendarEl.scrollTop = scrollTop; }, 0);
      }
      
    } catch (err) {
      if (err && (err.status === 409 || err.response?.status === 409)) {
        setDragDropError("Deze actie conflicteert met een bestaande afspraak!");
      } else {
        setDragDropError("Fout bij ontkoppelen van event: " + (err.message || err));
      }
    }
    
    setShowUncoupleDialog(false);
    setPendingUncoupleData(null);
  };

  const handleUncoupleCancel = () => {
    setShowUncoupleDialog(false);
    setPendingUncoupleData(null);
  };

  const isToday = day.format("DD-MM-YY") === dayjs().format("DD-MM-YY");
  const isWeekend = day.day() === 0 || day.day() === 6;
  const isOtherMonth = viewMode === "month" && day.month() !== monthIndex && !isToday;

  return (
    <div
      className={`border border-gray-100 dark:border-gray-800 flex flex-col overflow-hidden min-h-0 ${isWeekend ? "bg-gray-50 dark:bg-gray-900/70" : "dark:bg-gray-950"} ${isOtherMonth ? "opacity-40" : ""} ${isDragOver ? "ring-2 ring-inset ring-blue-400 bg-blue-50/50 dark:bg-blue-950/40" : ""}`}
      onDragOver={isReadonly ? undefined : (e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={isReadonly ? undefined : () => setIsDragOver(false)}
      onDrop={isReadonly ? undefined : async (e) => {
        e.preventDefault();
        setIsDragOver(false);
        setDragDropError(null);

        const newDayUtc = Date.UTC(
          day.year(),
          day.month(),
          day.date(),
          0, 0, 0, 0
        );

        const textPlain = e.dataTransfer.getData("text/plain") || "";
        const rosterRaw = e.dataTransfer.getData("application/x-onduty-roster")
          || (textPlain.startsWith("roster:") ? textPlain.slice("roster:".length) : "");
        if (rosterRaw) {
          if (!activeCalendarId) {
            setDragDropError("Geen kalender geselecteerd.");
            return;
          }
          let roster;
          try {
            roster = JSON.parse(rosterRaw);
          } catch {
            setDragDropError("Ongeldige persoon of shift.");
            return;
          }
          if (!roster.phoneNumber || !String(roster.phoneNumber).trim()) {
            setDragDropError("Vul eerst een doorschakelnummer in bij deze persoon of shift.");
            return;
          }
          const newEvent = {
            title: roster.name,
            description: "",
            label: roster.id,
            day: newDayUtc,
            allDay: false,
            startTime: "00:00",
            endTime: "23:59",
            phoneNumber: roster.phoneNumber,
            recurrenceType: null,
            calendarId: activeCalendarId,
          };
          const overlaps = dayEvents.some((evt) => isOverlap(evt, newEvent));
          if (overlaps) {
            setDragDropError("Tijd overlapt met een andere afspraak!");
            return;
          }
          try {
            await EventsService.saveEvent(newEvent, token);
            if (typeof fetchEvents === "function") {
              setTimeout(() => fetchEvents(), 0);
            }
          } catch (err) {
            setDragDropError("Fout bij inplannen: " + (err.message || err));
          }
          return;
        }

        const eventRaw = e.dataTransfer.getData("application/json");
        if (!eventRaw) return;
        const event = JSON.parse(eventRaw);
        
        // If it's a projected event, show confirmation dialog
        if (event.projection) {
          setPendingUncoupleData({
            originalEvent: event,
            newDay: newDayUtc
          });
          setShowUncoupleDialog(true);
          return;
        }
        
        // For regular events, proceed with normal move logic
        const updatedEvent = { ...event, day: newDayUtc };
        const overlaps = dayEvents.some(
          (evt) => evt.id !== updatedEvent.id && isOverlap(evt, updatedEvent)
        );
        if (overlaps) {
          setDragDropError("Tijd overlapt met een andere afspraak!");
          return;
        }
        try {
          // Save scroll position before refetch
          const calendarEl = document.getElementById('main-calendar-scroll');
          const scrollTop = calendarEl ? calendarEl.scrollTop : null;

          await EventsService.updateEvent(updatedEvent, token);
          // Always refetch events from the API after moving
          if (typeof fetchEvents === 'function') {
            setTimeout(() => fetchEvents(), 0);
          } else {
            dispatchCalEvent({ type: "update", payload: updatedEvent });
          }
          // Restore scroll after DOM update
          if (calendarEl && scrollTop !== null) {
            setTimeout(() => { calendarEl.scrollTop = scrollTop; }, 0);
          }
        } catch (err) {
          if (err && (err.status === 409 || err.response?.status === 409)) {
            setDragDropError("Deze actie conflicteert met een bestaande afspraak!");
          } else {
            setDragDropError("Fout bij opslaan van event: " + (err.message || err));
          }
        }
      }}
    >
      <header className="flex flex-col items-center flex-shrink-0 min-h-0">
        <p
          className={`text-xs md:text-sm p-0.5 md:p-1 my-0 md:my-1 text-center leading-none ${getCurrentDayClass()}${!isToday && isOtherMonth ? " text-gray-400" : ""}`}
        >
          {day.format("DD")}
        </p>
      </header>
      {dragDropError && (
        <div className="text-red-500 text-xs text-center mb-1">{dragDropError}</div>
      )}
      <div
        className={`flex-1 cursor-pointer relative min-h-0 ${isTimeBasedView ? "overflow-hidden" : "flex flex-col gap-0.5 px-0.5 pb-0.5 overflow-y-auto"}`}
        style={isTimeBasedView ? { minHeight: '1440px' } : {}} // 24 hours * 60px = 1440px
        onClick={() => {
          setDaySelected(day);
          // Only open modal if not readonly OR if clicking on an existing event (handled in event onClick)
          // So do nothing here for readonly users (blank space click)
          if (!isReadonly) {
            setShowEventModal(true);
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (!isReadonly) {
            const newDayUtc = Date.UTC(
              day.year(),
              day.month(),
              day.date(),
              0, 0, 0, 0
            );
            setContextMenu({
              show: true,
              x: e.pageX,
              y: e.pageY,
              type: 'day',
              event: null,
              day: newDayUtc,
            });
          }
        }}
      >
        {/* Hour lines for day/week view - only show in time-based views */}
        {isTimeBasedView && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 24 }, (_, i) => (
              <div 
                key={i}
                className="border-t border-gray-100"
                style={{ 
                  position: 'absolute', 
                  top: `${i * 60}px`, 
                  left: 0, 
                  right: 0,
                  height: '60px'
                }}
              />
            ))}
          </div>
        )}
        {(isTimeBasedView ? layoutTimedEvents(dayEvents) : dayEvents.map((evt, index) => ({ evt, index }))).map((item) => {
          const evt = item.evt;
          const labelObj = evt.label && Array.isArray(labels) ? labels.find(l => l.id === evt.label) : null;
          const bgColor = labelObj && labelObj.color ? labelObj.color : '#e5e7eb';
          return (
            <div
              key={evt.id != null ? `${evt.id}-${item.index}` : item.index}
              draggable={!isReadonly}
              onDragStart={isReadonly ? undefined : (e) => {
                e.dataTransfer.setData("application/json", JSON.stringify(evt));
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEvent(evt);
                setShowEventModal(true);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!isReadonly) {
                  setContextMenu({
                    show: true,
                    x: e.pageX,
                    y: e.pageY,
                    type: 'event',
                    event: evt,
                    day: null,
                  });
                }
              }}
              className={
                isTimeBasedView
                  ? "px-1 py-0.5 md:p-1 text-gray-700 rounded overflow-hidden z-10"
                  : "px-1 py-0.5 text-gray-700 rounded truncate leading-tight flex-shrink-0 min-w-0"
              }
              style={eventBlockStyle(item, bgColor)}
            >
              {isTimeBasedView ? (
                <>
                  {evt.startTime && evt.endTime ? (
                    <div className="text-xs">
                      {evt.startTime} - {evt.endTime}
                    </div>
                  ) : null}
                  <div className="text-sm font-semibold truncate">{evt.title}</div>
                  {evt.description && (
                    <div className="text-xs text-gray-600 truncate">{evt.description}</div>
                  )}
                  {evt.phoneNumber && (
                    <div className="text-xs text-gray-500 truncate">📞 {evt.phoneNumber}</div>
                  )}
                </>
              ) : (
                <div className="truncate text-xs">
                  <span className="font-semibold">{evt.title}</span>
                  {evt.startTime && evt.endTime ? (
                    <span className="text-gray-600"> {evt.startTime}–{evt.endTime}</span>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Confirmation dialog for uncoupling projected events */}
      {showUncoupleDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-lg p-6 m-4 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Terugkerende afspraak ontkoppelen
            </h3>
            <p className="text-gray-700 mb-6">
              Deze afspraak is onderdeel van een terugkerende serie. Door deze te verplaatsen wordt deze losgekoppeld van de serie en ontstaat er een nieuwe, losse afspraak.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleUncoupleCancel}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={handleUncoupleConfirm}
                className="px-4 py-2 text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
              >
                Ontkoppelen en verplaatsen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
